import {createSlice, createAsyncThunk} from '@reduxjs/toolkit'
import {AuthCheck, DownloadFileFn} from '../../helpers'
import {
  SearchCheckClient,
  GetCheckClient,
  CheckAddCollectionCheckClient,
  CheckAddPaymentCheckClient,
  UpdateCheckClient,
} from '../../api'

const SERVICE_URL = window.ENVVARS.REACT_APP_SERVICE_URL

const searchCheckClient = new SearchCheckClient(SERVICE_URL)
const getCheckClient = new GetCheckClient(SERVICE_URL)
const checkAddCollectionCheckClient = new CheckAddCollectionCheckClient(SERVICE_URL)
const checkAddPaymentCheckClient = new CheckAddPaymentCheckClient(SERVICE_URL)
const updateCheckClient = new UpdateCheckClient(SERVICE_URL)

const initialState = {
  loading: false,
  isRedirect: false,
  error: null,
  isDeleted: false,
  isArchived: false,
  transfer: false,
  excel: null,
  check: null,
  detail: null,
}

// Async Thunks
export const checkGetAll = createAsyncThunk('check/getAll', async (params, {dispatch}) => {
  try {
    dispatch(setLoading(true))
    const response = await searchCheckClient.search(
      params.name,
      params.checkTypeId,
      params.defermentStartDate,
      params.defermentEndDate,
      params.companyId,
      params.checkStateId,
      params.items_per_page,
      params.page,
      params.order,
      params.sort
    )
    dispatch(setCheckList(response))
    return response
  } catch (error) {
    AuthCheck(error)
    dispatch(setError(error))
    throw error
  }
})

export const checkGetId = createAsyncThunk('check/getById', async (id, {dispatch}) => {
  try {
    dispatch(setLoading(true))
    const response = await getCheckClient.get(id)
    dispatch(setCheckDetail(response))
    return response
  } catch (error) {
    AuthCheck(error)
    dispatch(setError(error))
    throw error
  }
})

export const checkAddCollection = createAsyncThunk(
  'check/addCollection',
  async (params, {dispatch}) => {
    try {
      dispatch(setLoading(true))
      const response = await checkAddCollectionCheckClient.create(params)
      dispatch(setTransfer(response))
      return response
    } catch (error) {
      AuthCheck(error)
      dispatch(setError(error))
      throw error
    }
  }
)

export const checkAddPayment = createAsyncThunk('check/addPayment', async (params, {dispatch}) => {
  try {
    dispatch(setLoading(true))
    const response = await checkAddPaymentCheckClient.create(params)
    dispatch(setTransfer(response))
    return response
  } catch (error) {
    AuthCheck(error)
    dispatch(setError(error))
    throw error
  }
})

export const checkExcel = createAsyncThunk(
  'check/excel',
  async ({params, fileName}, {dispatch}) => {
    try {
      dispatch(setLoading(true))
      const response = await searchCheckClient.excel(
        params.name,
        params.checkTypeId,
        params.defermentStartDate,
        params.defermentEndDate,
        params.companyId,
        params.checkStateId,
        params.items_per_page,
        params.page,
        params.order,
        params.sort
      )
      if (response && response.data) {
        DownloadFileFn(response.data, fileName)
      }
      dispatch(setExcel(response))
      return response
    } catch (error) {
      AuthCheck(error)
      dispatch(setError(error))
      throw error
    }
  }
)

export const checkUpdate = createAsyncThunk('check/update', async (params, {dispatch}) => {
  try {
    dispatch(setLoading(true))
    const response = await updateCheckClient.update(params)
    dispatch(setCheckUpdated({check: response, params}))
    return response
  } catch (error) {
    AuthCheck(error)
    dispatch(setError(error))
    throw error
  }
})

// Slice
const checkSlice = createSlice({
  name: 'check',
  initialState,
  reducers: {
    setLoading: (state, action) => {
      state.loading = action.payload
    },
    setError: (state, action) => {
      state.error = action.payload
      state.loading = false
    },
    setCheckList: (state, action) => {
      state.check = action.payload
      state.loading = false
    },
    setCheckDetail: (state, action) => {
      state.detail = action.payload
      state.loading = false
    },
    setTransfer: (state, action) => {
      state.transfer = action.payload || true
      state.loading = false
    },
    setDeleted: (state) => {
      state.isDeleted = true
      state.loading = false
    },
    setCheckCreated: (state, action) => {
      state.isRedirect = action.payload
      state.loading = false
    },
    setCheckUpdated: (state, action) => {
      state.isRedirect = action.payload.check || action.payload.params
      state.detail = action.payload.params
      state.loading = false
    },
    setExcel: (state, action) => {
      state.excel = action.payload
      state.loading = false
    },
    resetState: (state) => {
      Object.assign(state, initialState)
    },
  },
  extraReducers: (builder) => {
    builder.addMatcher(
      (action) => action.type.startsWith('check/') && action.type.endsWith('/pending'),
      (state) => {
        state.loading = true
      }
    )
  }
})

export const {
  setLoading,
  setError,
  setCheckList,
  setCheckDetail,
  setTransfer,
  setDeleted,
  setCheckCreated,
  setCheckUpdated,
  setExcel,
  resetState,
} = checkSlice.actions

// Selectors
export const selectCheckList = (state) => state.check.check
export const selectCheckLoading = (state) => state.check.loading
export const selectCheckError = (state) => state.check.error
export const selectCheckDetail = (state) => state.check.detail
export const selectCheckTransfer = (state) => state.check.transfer
export const selectCheckExcel = (state) => state.check.excel

export default checkSlice.reducer
